<script setup lang="ts">
import { computed, readonly, defineEmits } from 'vue';
import Modal from '@/components/shared/Dialog.vue';
import ModalControls from '@/components/modals/shared/ModalControls.vue';
import i18n from '@/plugins/i18n';
import { useToast } from '@/composables/useToast';
import { useStore } from 'vuex';
import { JobRoles, UserResponseDto } from '@clearbanc/baus-sdk';
import { RequestStatus } from '@/types/request.types';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

// Props
const props = defineProps<{
  visible: boolean;
  userToDelete: Pick<
    UserResponseDto,
    'id' | 'fullName' | 'isPrimary' | 'jobRoles'
  >;
}>();

// Setup
const store = useStore();
const toast = useToast();
const emit = defineEmits(['closeModal', 'deleteSuccess']);

// Methods
const closeModal = () => {
  emit('closeModal');
};

const softDeleteUser = async () => {
  const { userToDelete } = props;
  const fullName = userToDelete?.fullName;

  await store.dispatchApiAction('DELETE_USER', { id: userToDelete.id });

  if (requestStatuses.deleteUser.isSuccess) {
    analytics.track(SEGMENT_EVENTS.if_user_deleted, {
      deleted_user_id: userToDelete.id,
    });
    toast.success(
      i18n.t('teamPage.deleteUserModal.successMessage', { fullName }),
    );
    emit('deleteSuccess');
    closeModal();
  } else {
    toast.error(
      i18n.t('teamPage.deleteUserModal.failureMessage', { fullName }),
    );
    closeModal();
  }
};

// Computed Properties
const requestStatuses = readonly({
  deleteUser: computed<RequestStatus>(() =>
    store.getters.requestStatus('DELETE_USER'),
  ),
  getAdvances: computed<RequestStatus>(() =>
    store.getters.requestStatus('GET_USER_ADVANCES'),
  ),
});
const userIsSignatory = computed<Boolean>(() => {
  return props.userToDelete?.jobRoles.includes(JobRoles.Signatory);
});

const modalContent = computed(() => {
  if (props.userToDelete?.isPrimary) {
    return {
      message: i18n.t('teamPage.deleteUserModal.primaryUser.message', {
        fullName: props.userToDelete?.fullName,
      }),
      buttons: [
        {
          text: 'Got it',
          action: closeModal,
          classes: ['p-button-outlined'],
        },
      ],
    };
  } else if (userIsSignatory.value) {
    return {
      message: i18n.t('teamPage.deleteUserModal.cannotDeleteSignatoryMessage', {
        fullName: props.userToDelete?.fullName,
      }),
      buttons: [
        {
          text: 'Got it',
          action: closeModal,
          classes: ['p-button-outlined'],
        },
      ],
    };
  } else {
    return {
      message: i18n.t('teamPage.deleteUserModal.teamMember.message', {
        fullName: props.userToDelete?.fullName,
      }),
      buttons: [
        {
          text: 'Cancel',
          action: closeModal,
          classes: ['p-button-outlined'],
        },
        {
          text: 'Delete',
          action: softDeleteUser,
          classes: ['p-button-danger'],
          loading: requestStatuses.deleteUser.isPending,
          attributes: {
            loading: requestStatuses.deleteUser.isPending,
            disabled: requestStatuses.deleteUser.isPending,
          },
        },
      ],
    };
  }
});
</script>

<template>
  <Modal
    :visible="visible"
    :header="
      props.userToDelete?.isPrimary
        ? i18n.t('teamPage.deleteUserModal.primaryUser.header')
        : i18n.t('teamPage.deleteUserModal.teamMember.header')
    "
    :modal="true"
    :closable="false"
    :class="$style.modal"
  >
    <p>{{ modalContent.message }}</p>
    <template #footer>
      <ModalControls :buttons="modalContent.buttons" />
    </template>
  </Modal>
</template>

<style lang="less" module>
.modal {
  max-width: 665px !important;
}
</style>
